import { lazy, Suspense, useEffect, useMemo } from 'react'
import { Navigate, Route, Routes as RouterRoutes, useLocation } from 'react-router-dom'

import { Routes } from '@/utils/navigation/routes'
import { trackPageView } from '@/utils/analytics'
import { gaScreen, screenNumber } from '@/utils/ga'
import { pixelsPageView } from '@/utils/pixels'

import { AppErrorBoundary } from '@/components/AppErrorBoundary/AppErrorBoundary'
import { onLocalHost } from '@/utils/localHost'
import { AuthKnown, RequireAuth } from '@/components/RequireAuth/RequireAuth'
import { wizardNeedsSignIn } from '@/utils/authGate'
import { Landing } from '@/containers/Landing/Landing'
import { track } from '@/utils/analytics'
import { consumeStaleChunkReload, lazyRoute } from '@/utils/lazyRoute'
import { FUNNEL_TYPE } from '@/utils/funnel'
import { funnelDetour, productFirst } from '@/utils/spec/funnelPath'
import { hasWorkspaceHere } from '@/utils/spec/workspaceHere'
import { confirmedSyncApplies } from '@/utils/spec/confirmedPurchaseRules'
import { loadWizardState } from '@/utils/spec/specStorage'

// The landing is the public entry, so it ships in the first chunk; the three logged-in surfaces
// load on demand — the console alone carries every picker and the whole row renderer, and the
// workspace is only ever reached after a purchase.
//
// `lazyRoute`, не голый `lazy`: чанк, пропавший после пересборки, перезагружает страницу один раз
// вместо белого экрана — замер и причина в utils/lazyRoute.ts.
const Onboard = lazyRoute(() =>
  import('@/containers/Onboard/Onboard').then((m) => ({ default: m.Onboard })),
)
const AppWizard = lazyRoute(() =>
  import('@/containers/AppWizard/AppWizard').then((m) => ({ default: m.AppWizard })),
)
const Console = lazyRoute(() =>
  import('@/containers/Console/Console').then((m) => ({ default: m.Console })),
)
const Home = lazyRoute(() => import('@/containers/Home/Home').then((m) => ({ default: m.Home })))
/* Воронка 4 (SaaS): свой лендинг и регистрация почтой. Лениво — остальным воронкам этот код не нужен. */
const LandingSaas = lazyRoute(() =>
  import('@/containers/LandingSaas/LandingSaas').then((m) => ({ default: m.LandingSaas })),
)
const SignUp = lazyRoute(() => import('@/containers/SignUp/SignUp').then((m) => ({ default: m.SignUp })))
/* Разблокировка платного для себя (utils/devUnlock.ts): чанк существует в сборке, но запрашивается
   только на localhost / 127.0.0.1 — решение принято до рендера, по имени хоста.
   Простой `lazy`, а не lazyRoute: удачная загрузка lazyRoute снимает замок «одна перезагрузка на
   пропавший чанк» (lazyRoute.markRouteLoaded), и плашка, загрузившись раньше сломанного маршрута,
   снимала его каждый раз — страница перезагружалась по кругу (ревью, 2026-09-23). */
const DevUnlock = onLocalHost()
  ? lazy(() => import('@/components/DevUnlock/DevUnlock').then((m) => ({ default: m.DevUnlock })))
  : null
const ThankYou = lazyRoute(() =>
  import('@/containers/ThankYou/ThankYou').then((m) => ({ default: m.ThankYou })),
)
const Login = lazyRoute(() => import('@/containers/Login/Login').then((m) => ({ default: m.Login })))
const Unsubscribed = lazyRoute(() =>
  import('@/containers/Unsubscribed/Unsubscribed').then((m) => ({ default: m.Unsubscribed })),
)
/* Поиск по ссылке «поделиться» — страница для тех, кому ссылку прислали; остальным её код не нужен. */
const SharePage = lazyRoute(() => import('@/containers/Share/SharePage').then((m) => ({ default: m.SharePage })))

export const App = () => {
  const location = useLocation()
  /* Заход считают ЗДЕСЬ все три потребителя: «пиксель на всех страницах флоу» в SPA — это смена
     маршрута, а не загрузка документа, иначе Мета видела бы один PageView на весь путь от лендинга
     до пейволла. Шаги визарда — тоже маршруты, так что поэкранный отчёт собирается отсюда же.

     Amplitude в этом списке не было, и это стоило дорого: его первое событие висело на КЛИКЕ по
     кнопке, поэтому платный трафик, который зашёл и ушёл, не оставлял в нём ни строчки — пиксели и
     GA визит видели, Amplitude молчал, и при работающей рекламе продукт выглядел мёртвым. */
  /* Вкладка только что перезагрузилась сама из-за пропавшего чанка — посчитать это отсюда, с новой
     страницы: со старой событие умерло бы вместе с её батчем. Число — цена деплоя в людях. */
  useEffect(() => {
    if (consumeStaleChunkReload()) track('app_crashed', { kind: 'stale_chunk', path: window.location.pathname })
  }, [])

  /* Оплата, подтверждённая на сервере, о которой этот браузер не знает (utils/spec/confirmedPurchases.ts): возврат
     с формы оплаты к нам не доходит, и без этого оплативший сидел за пейволлом до ссылки возврата. Записали —
     открыть кабинет ПОЛНОЙ загрузкой, как это делает /thank-you: экраны читают покупку из хранилища при создании,
     и переход внутри SPA показал бы прежний пейволл. Модуль грузится отдельным чанком и только в браузере, где
     кабинет или прогон уже есть: лендингу первого захода он не нужен. */
  useEffect(() => {
    if (!confirmedSyncApplies({ pathname: window.location.pathname, hasWorkspace: hasWorkspaceHere(), wizardRun: loadWizardState().runId })) return
    void import('@/utils/spec/confirmedPurchases')
      .then((module) => module.syncConfirmedPurchases())
      .then((written) => {
        if (written > 0) window.location.replace(Routes.home)
      })
      .catch(() => null)
  }, [])

  /* Чужая дверь этой воронки — /signup в 1–3, /onboard в 4, /home в 4 без кабинета (funnelPath.ts,
     funnelDetour). Решается ЗДЕСЬ, до экрана: экран, который уводил сам, успевал отправить свои
     события, а App — заход за адрес, которого человек так и не увидел. По адресу, а не на каждый
     рендер: внутри кабинета параметры меняются на каждый клик, а ответ от них не зависит. */
  const detour = useMemo(() => funnelDetour(location.pathname, FUNNEL_TYPE, hasWorkspaceHere), [location.pathname])

  useEffect(() => {
    /* С этого адреса сейчас уведут — это не заход, и считать его значило бы писать в воронку экран,
       которого не было (page_viewed `/signup` у воронки 1, `/onboard` у воронки 4). Заход посчитается
       следующим проходом, уже за адрес, куда увели. */
    if (detour) return
    trackPageView(location.pathname, screenNumber(location.pathname))
    pixelsPageView()
    gaScreen(location.pathname)
  }, [location.pathname, detour])

  /* Уводя, адрес несёт с собой query и hash байт в байт. Метки кампании acquisition.ts снял ещё до
     рендера, но fbevents.js грузится лениво, на первом PageView, и `fbclid` читает из адреса уже ПОСЛЕ
     увода: потерянный query — это потерянная cookie `_fbc`, то есть клик, который Мета не свяжет с
     объявлением. Метки сплита `?r=` и `?v=` сервер уже прочитал, их снимает maskParams.ts. */
  const carried = { search: location.search, hash: location.hash }

  return (
  <AppErrorBoundary resetKey={location.pathname}>
  <Suspense fallback={null}>
    {/* Чужая дверь — до роутера: экран за ней не монтируется и его чанк даже не запрашивается. */}
    {detour ? (
      <Navigate to={{ ...carried, pathname: detour }} replace />
    ) : (
    <RouterRoutes>
      {/* Дверь одна, лендинга два: воронка 4 получает свой (см. utils/spec/funnelPath.ts,
          productFirst). Ветвление здесь, а не внутри Landing: старый лендинг ничего не знает о
          воронках и не должен. */}
      <Route path={Routes.landing} element={productFirst(FUNNEL_TYPE) ? <LandingSaas /> : <Landing />} />
      <Route path={Routes.signup} element={<SignUp />} />
      <Route path={Routes.onboard} element={<Onboard />} />
      <Route path={Routes.app} element={<Navigate to="/app/product" replace />} />
      {/* Вход через Intch (где просить код — utils/authGate.ts). Кабинет закрыт всегда: он и есть
          аккаунт. Выдача — только при коде сразу после почты; при коде у кабинета она анонимна, как до
          входа. «Спасибо» не закрыта никогда: оплативший, упёршись во вход, остался бы без тарифа и
          без Purchase, пока не введёт код (routes/pay.ts принимает возврат без пользователя, тариф
          переходит к человеку при входе). Она лишь ждёт ответа «вошёл ли», чтобы вошедшему сверить
          заказ с сервером. Лендинг, онбординг, вход и отписка открыты; консоль входит ключом оператора. */}
      <Route path={Routes.appStep} element={wizardNeedsSignIn() ? <RequireAuth><AppWizard /></RequireAuth> : <AppWizard />} />
      <Route path={Routes.home} element={<RequireAuth><Home /></RequireAuth>} />
      <Route path={Routes.thankYou} element={<AuthKnown><ThankYou /></AuthKnown>} />
      <Route path={Routes.login} element={<Login />} />
      <Route path={Routes.console} element={<Console />} />
      <Route path={Routes.unsubscribed} element={<Unsubscribed />} />
      <Route path={Routes.share} element={<SharePage />} />
      {/* Опечатка в адресе объявления не должна стоить меток: на лендинг — с тем же query. Заход
          на кривой адрес при этом считается (page_viewed с этим path) — и это полезно: так битая
          ссылка в объявлении видна в Amplitude в первый же день. */}
      <Route path="*" element={<Navigate to={{ ...carried, pathname: Routes.landing }} replace />} />
    </RouterRoutes>
    )}
  </Suspense>
  {/* Плашка Local — только на loopback-хосте и отдельным чанком: на проде её кода в браузере нет. */}
  {DevUnlock ? (
    <Suspense fallback={null}>
      <DevUnlock />
    </Suspense>
  ) : null}
  </AppErrorBoundary>
  )
}
